import { formatTokenAmount, STORE_LIMITS, type StoreItemView, type StorePayInstruction, type StorePaymentView, type StoreWorn } from "@common/platform/store";
import $ from "jquery";
import { GameConsole } from "../console/gameConsole";
import { showStoreEmblem } from "../emblemEditor";
import { InputManager } from "../managers/inputManager";
import { t, tr } from "../utils/translations/phrases";
import { sendStorePayment, storeBuyer, storeBuyerChanged } from "../walletFlow";
import { isMarketId } from "@common/platform/market";
import { MarketMenu } from "./marketMenu";
import { StoreCatalog } from "./storeCatalog";

/*
 * The store in the menu (0.92): the items on sale (picture emblems, voice reactions), each one's price in the game's
 * coin and what's left of it; buying (the player's own wallet burns the price with the coin's own burn, 0.95: sent to the
 * dead address until then, and for a coin without one; the server finds the burn before the item is theirs); and what the
 * player owns, to wear: one picture emblem (or their own drawing), and up to six reactions for the wheel in a match.
 *
 * One payment, one transfer (0.93 reviews). The wallet is asked to pay a payment only when nothing this device knows of
 * may still pay it:
 * - the transaction the wallet sends is remembered on this device (a week and a day) before anything else, and told to
 *   the store again when the payment is asked for again (a second Buy, a page that couldn't tell the store, a reload);
 * - one the store dropped (refused, or lost in a restart) is told again before the wallet is asked, and forgotten only
 *   when the store refuses it again; one sent after its payment's time, likewise, before a new payment for the item;
 * - one the wallet may have sent (it answered with an error after it was asked to send) holds the payment to checks for
 *   ten minutes, as one being paid in another tab does (`trenchio_store_paying`);
 * - the payment's card stays busy while it's paid, followed or looked for (its wallet step open in this tab, after a
 *   wallet switched away and back too), and Pay is offered only with five minutes left at least (the wallet's step, its
 *   block);
 * - a look the page pays after is one the store made whole (it says so): one answered as it was, asked too soon after
 *   another, is asked again; a transaction is forgotten only when the store refused that one, and one for an item the
 *   wallet has already is never paid again;
 * - a wallet switched while one runs stops it: nothing of one wallet's is paid, said or kept for another.
 */

/** what the store says to send for a payment: the coin's burn of its price (0.95), or a transfer of it (see sendStorePayment) */
type PayInstruction = StorePayInstruction;

type MinePayment = StorePaymentView & { readonly pay?: PayInstruction };

interface Mine {
    readonly owned: readonly string[]
    readonly worn: StoreWorn
    readonly payments: readonly MinePayment[]
    readonly walletRequired?: boolean
}

/** a payment as the store answered a page's look or transaction: and what became of that transaction, when it looked */
interface Answer {
    readonly payment: StorePaymentView
    /** owned: it paid nothing, the wallet has the item already (nothing more to pay) */
    readonly told?: "paid" | "waiting" | "refused" | "owned" | "unknown"
    /** the transaction `told` is about */
    readonly toldTx?: string
    /** false: not looked up whole now (asked too soon after another look, the chain silent, a search cut short) */
    readonly looked?: boolean
}

/** (the flea market, 0.98: players' own emblems, marketMenu.ts) */
type Tab = "emblem" | "reaction" | "market" | "mine";

const EMPTY: Mine = { owned: [], worn: { reactions: [] }, payments: [] };
let mine: Mine = EMPTY;
/** the wallet whose items `mine` holds (read from the store, or kept from its answers) */
let mineFor: string | undefined;
let tab: Tab = "emblem";
/** what each item's card says now (a purchase on its way, an error) */
const said = new Map<string, { readonly text: string, readonly bad?: boolean }>();
/** the purchase on its way (one at a time): its item, and whether it's being followed (its transaction sent) */
let purchaseOn: { readonly id: number, readonly itemId: string } | undefined;
let purchases = 0;
let following = false;
/** the payments being looked for at the player's word (I paid: check, Look again) */
const checking = new Set<string>();
/** bumped when another wallet signs in: whatever the one before had on its way stops */
let generation = 0;
let lastBuyer: string | undefined;
let preview: HTMLAudioElement | undefined;
let previewTimer: ReturnType<typeof setTimeout> | undefined;
/** what had the focus before the store opened (it gets it back when the store closes) */
let opener: HTMLElement | undefined;

const element = <T extends HTMLElement = HTMLElement>(id: string): T | null => document.getElementById(id) as T | null;
const escapeHtml = (text: string): string => text.replace(/[&<>"']/gu, char => `&#${char.charCodeAt(0)};`);
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));
/** the key the voice reactions' wheel opens with (X unless the player changed it), or none */
const reactionKey = (): string | undefined => InputManager.binds.getInputsBoundToAction("+reaction_wheel").find(key => key !== "");

// ---- what a run (a purchase, a check) was started for: another wallet signed in since, it stops where it is
interface Run {
    readonly owner: string | undefined
    readonly generation: number
}
/** a run stopped (another wallet signed in): nothing more done, nothing said */
class Stopped extends Error {}
const startRun = (): Run => ({ owner: storeBuyer()?.address, generation });
const alive = (run: Run): boolean => run.generation === generation && run.owner !== undefined && storeBuyer()?.address === run.owner;
const stillOn = (run: Run): void => {
    if (!alive(run)) throw new Stopped();
};

// ---- what this device remembers, shared by its tabs (each part a small JSON record in its storage)
const readRecord = <T>(key: string): Record<string, T> => {
    try {
        const saved: unknown = JSON.parse(localStorage.getItem(key) ?? "{}");
        return saved && typeof saved === "object" && !Array.isArray(saved) ? saved as Record<string, T> : {};
    } catch {
        return {};
    }
};
const writeRecord = <T>(key: string, change: (all: Record<string, T>) => void): boolean => {
    try {
        const all = readRecord<T>(key);
        change(all);
        localStorage.setItem(key, JSON.stringify(all));
        return true;
    } catch {
        return false;
    }
};

// ---- the transactions the wallet sent, per payment (a week and a day: a late payment is looked for a week)
interface Sent {
    readonly tx: string
    /** when it was sent */
    readonly at: number
    /** the store said it has it */
    readonly told?: boolean
    /**
     * the store had it and has it no more, the payment still waiting: refused (failed), or lost (a restart, a second one
     * said). Told once more before the wallet is asked again: a lost one still on its way is never paid twice
     */
    readonly dropped?: boolean
}
const SENT_KEY = "trenchio_store_sent";
const SENT_KEPT_MS = STORE_LIMITS.latePaymentMs + 24 * 60 * 60_000;
const HASH = /^0x[0-9a-f]{64}$/u;
/** (this page's copy: storage refused, or not read yet) */
const sentHere = new Map<string, Sent>();
const sentOf = (paymentId: string): Sent | undefined => {
    const entry = readRecord<Partial<Sent>>(SENT_KEY)[paymentId];
    if (entry && typeof entry.tx === "string" && HASH.test(entry.tx) && typeof entry.at === "number" && Date.now() - entry.at < SENT_KEPT_MS)
        return { tx: entry.tx, at: entry.at, ...(entry.told === true ? { told: true } : {}), ...(entry.dropped === true ? { dropped: true } : {}) };
    return sentHere.get(paymentId);
};
const keepSent = (paymentId: string, entry: Sent): void => {
    sentHere.set(paymentId, entry);
    writeRecord<Sent>(SENT_KEY, all => {
        all[paymentId] = entry;
        for (const [id, each] of Object.entries(all)) if (!(Date.now() - Number(each?.at) < SENT_KEPT_MS)) delete all[id];
    });
};
const rememberSent = (paymentId: string, tx: string, told = false): void => keepSent(paymentId, { tx, at: sentOf(paymentId)?.at ?? Date.now(), ...(told ? { told } : {}) });
const dropSent = (paymentId: string): void => {
    const entry = sentOf(paymentId);
    if (entry && !entry.dropped) keepSent(paymentId, { tx: entry.tx, at: entry.at, dropped: true });
};
const forgetSent = (paymentId: string): void => {
    sentHere.delete(paymentId);
    writeRecord<Sent>(SENT_KEY, all => {
        delete all[paymentId];
    });
};
/** the transaction sent for a payment: the store's word for it, or this device's (none: never sent, or dropped) */
const sentFor = (payment: StorePaymentView): string | undefined => {
    const known = payment.tx?.toLowerCase();
    if (known && HASH.test(known)) return known;
    const here = sentOf(payment.id);
    if (!here || here.dropped) return undefined;
    // (the store had it and has it no more, the payment still waiting: dropped, and told again before it's paid again)
    if (here.told && payment.state === "waiting") {
        dropSent(payment.id);
        return undefined;
    }
    return here.tx;
};
/** a transaction this device sent for a payment, that the store dropped since */
const droppedFor = (paymentId: string): string | undefined => {
    const here = sentOf(paymentId);
    return here?.dropped ? here.tx : undefined;
};

// ---- payments the wallet may have sent (an error after it was asked to send), and payments being paid in a tab
const MAYBE_KEY = "trenchio_store_maybe";
const MAYBE_SENT_MS = 10 * 60_000;
const PAYING_KEY = "trenchio_store_paying";
const PAYING_ELSEWHERE_MS = 5 * 60_000;
const maybeHere = new Map<string, number>();
const fresh = (at: unknown, ms: number): boolean => typeof at === "number" && Date.now() - at < ms && at <= Date.now() + 60_000;
const maybeSent = (paymentId: string): boolean => fresh(readRecord<number>(MAYBE_KEY)[paymentId] ?? maybeHere.get(paymentId), MAYBE_SENT_MS);
const markMaybeSent = (paymentId: string): void => {
    maybeHere.set(paymentId, Date.now());
    writeRecord<number>(MAYBE_KEY, all => {
        all[paymentId] = Date.now();
        for (const [id, at] of Object.entries(all)) if (!fresh(at, MAYBE_SENT_MS)) delete all[id];
    });
};
/** the payments this tab's wallet step is on (another tab's: the store's card says it's being paid there) */
const payingHere = new Set<string>();
const payingElsewhere = (paymentId: string): boolean => !payingHere.has(paymentId) && fresh(readRecord<number>(PAYING_KEY)[paymentId], PAYING_ELSEWHERE_MS);
const markPaying = (paymentId: string): void => {
    payingHere.add(paymentId);
    writeRecord<number>(PAYING_KEY, all => {
        all[paymentId] = Date.now();
        for (const [id, at] of Object.entries(all)) if (!fresh(at, PAYING_ELSEWHERE_MS)) delete all[id];
    });
};
const clearPaying = (paymentId: string): void => {
    payingHere.delete(paymentId);
    writeRecord<number>(PAYING_KEY, all => {
        delete all[paymentId];
    });
};

/** the store's clock against this device's (its answers' Date): a payment's times are the store's */
let clockSkew = 0;
const storeNow = (): number => Date.now() + clockSkew;
/** a payment is paid by the wallet only with this long left at least (the wallet's step, its block, a little more) */
const PAY_MARGIN_MS = 5 * 60_000;
const timeToPay = (payment: StorePaymentView): boolean => payment.expiresAt - storeNow() > PAY_MARGIN_MS;
/** a transaction sent this long ago or less, not in a block yet, may still be: a new payment waits for the store's word */
const STILL_ON_ITS_WAY_MS = 30 * 60_000;
/** the store looks a payment up for a page once in 3 s at most: a look sooner is answered unlooked */
const LOOK_SPACING_MS = 3_100;
const lookedAt = new Map<string, number>();

/** a request that got no answer at all (the browser's own words for it were shown: "Failed to fetch"; 0.94 audit) */
const noAnswerWords = (): string => t("The game server couldn't be reached: check your connection");
/** words of the store's own (an error, a payment's note, why it can't sell): in the player's language when the phrasebook has them */
const serverWords = (words: string | undefined): string | undefined => (words ? tr(words) : words);
const api = async <T>(path: string, init?: RequestInit): Promise<T> => {
    let response: Response;
    try {
        response = await fetch(`${API_URL}${path}`, {
            credentials: "include",
            cache: "no-store",
            ...init,
            headers: { ...(init?.body ? { "Content-Type": "application/json" } : {}), ...(init?.headers ?? {}) }
        });
    } catch {
        throw new Error(noAnswerWords());
    }
    const date = Date.parse(response.headers.get("Date") ?? "");
    if (Number.isFinite(date)) clockSkew = date - Date.now();
    const body = (await response.json().catch(() => ({}))) as T & { error?: string };
    if (!response.ok) throw new Error(serverWords(body.error) ?? t("The game server answered <status>", { status: response.status }));
    return body;
};
/** a request the store looks up (its key: a payment, and the transaction said, if one is), spaced so that it really looks */
const lookUp = async(key: string, spaced: boolean, request: () => Promise<Answer>): Promise<Answer> => {
    const wait = LOOK_SPACING_MS - (Date.now() - (lookedAt.get(key) ?? Number.NEGATIVE_INFINITY));
    if (spaced && wait > 0) await sleep(wait);
    // (when it's sent: the store's time for it)
    lookedAt.set(key, Date.now());
    return request();
};
/** the store looks for a payment (its transaction, or the coin's records); `spaced`: so that it really looks */
const look = (paymentId: string, spaced = false): Promise<Answer> =>
    lookUp(`${paymentId}:`, spaced, () => api<Answer>(`/store/payments/${encodeURIComponent(paymentId)}/check`, { method: "POST" }));
/** the store told a transaction sent for a payment (which it looks up); `spaced`: so that it really looks */
const tell = (paymentId: string, tx: string, spaced = false): Promise<Answer> =>
    lookUp(`${paymentId}:${tx}`, spaced, () => api<Answer>(`/store/payments/${encodeURIComponent(paymentId)}/sent`, { method: "POST", body: JSON.stringify({ tx }) }));

/** the player's own items and payments (nothing for a guest or nobody; never another wallet's) */
const loadMine = async(): Promise<void> => {
    const buyer = storeBuyer();
    if (!buyer || buyer.guest || !buyer.verified) {
        if (mineFor !== undefined) said.clear();
        mine = { ...EMPTY, walletRequired: true };
        mineFor = undefined;
        return;
    }
    // (another wallet than the one read: nothing of the earlier one's shown meanwhile, nor if this read fails)
    if (mineFor !== buyer.address) {
        mine = EMPTY;
        mineFor = buyer.address;
        said.clear();
    }
    try {
        const read = await api<Mine>("/store/mine");
        if (storeBuyer()?.address !== buyer.address) return;
        mine = read;
    } catch {
        // (kept as it was: the next look tries again)
    }
};

/** a payment the store just answered with, kept with the player's (its card knows it's waiting, whatever /mine says) */
const keepPayment = (payment: MinePayment): void => {
    if (mineFor === undefined || mineFor !== storeBuyer()?.address) return;
    const before = mine.payments.find(other => other.id === payment.id);
    const pay = payment.pay ?? before?.pay;
    mine = { ...mine, payments: [{ ...payment, ...(pay ? { pay } : {}) }, ...mine.payments.filter(other => other.id !== payment.id)] };
};

/** the picture emblem the wallet wears, on the menu's player too (the Customize box), or the drawn one */
const showWornPicture = async(): Promise<void> => {
    const id = mine.worn.emblem;
    // (a flea market emblem the wallet bought, 0.98: its picture, from the market)
    const bought = id && isMarketId(id) ? MarketMenu.emblem(id) : undefined;
    const item = bought ? { kind: "emblem" as const, name: bought.name, url: bought.url } : id ? StoreCatalog.item(id) : undefined;
    if (item?.kind !== "emblem") {
        showStoreEmblem(undefined);
        return;
    }
    const image = new Image();
    image.decoding = "async";
    image.src = item.url;
    try {
        await image.decode();
    } catch {
        return;
    }
    // (still the one worn, by the time it's read)
    if (mine.worn.emblem === id) showStoreEmblem({ name: item.name, image });
};

const amount = (atomic: string): string => {
    const token = StoreCatalog.view?.token;
    return token ? `${formatTokenAmount(atomic, token.decimals)} ${token.symbol}` : "";
};

const waitingFor = (itemId: string): MinePayment | undefined => mine.payments.find(payment => payment.itemId === itemId && payment.state === "waiting");
/** one given up that was paid late after all, its page closed: looked for again when the buyer asks (for a week) */
const lateFor = (itemId: string): MinePayment | undefined => mine.payments.find(payment => payment.itemId === itemId && payment.state === "expired" && storeNow() - payment.expiresAt < STORE_LIMITS.latePaymentMs);
/**
 * a given-up payment of the item whose transaction this device sent a moment ago: it may still pay it. A dropped one
 * too (0.93 review: lost in a restart, or another refused in its place): forgotten only when the store refuses it
 */
const lateSentFor = (itemId: string): { readonly payment: MinePayment, readonly tx: string } | undefined => {
    for (const payment of mine.payments) {
        if (payment.itemId !== itemId || payment.state !== "expired" || !(storeNow() - payment.expiresAt < STORE_LIMITS.latePaymentMs)) continue;
        const sent = sentOf(payment.id);
        if (sent && Date.now() - sent.at < STILL_ON_ITS_WAY_MS) return { payment, tx: sent.tx };
    }
    return undefined;
};

const card = (item: StoreItemView): string => {
    const owned = mine.owned.includes(item.id);
    const waiting = owned ? undefined : waitingFor(item.id);
    const late = owned || waiting ? undefined : lateFor(item.id);
    const note = said.get(item.id);
    const open = StoreCatalog.view?.open === true && !StoreCatalog.view?.problem;
    const art = item.kind === "emblem"
        ? `<img class="store-art" src="${escapeHtml(item.url)}" alt="" loading="lazy" decoding="async">`
        : `<button type="button" class="store-art store-play" data-play="${escapeHtml(item.id)}" aria-label="${escapeHtml(t("Play <name>", { name: item.name }))}"><i class="fa-solid fa-volume-high"></i></button>`;
    const checkButton = (payment: MinePayment, words: string, kind = "store-buy"): string =>
        `<button type="button" class="${kind}" data-check="${escapeHtml(payment.id)}" data-item="${escapeHtml(item.id)}">${words}</button>`;
    const busy = (words: string): string => `<button type="button" class="store-buy" disabled>${words}</button>`;
    // (a payment of the item being looked for: nothing else is done with it meanwhile)
    const looking = mine.payments.some(payment => payment.itemId === item.id && checking.has(payment.id));
    let action: string;
    let lateButton = late ? checkButton(late, t("Paid it already? Look again"), "store-late") : "";
    if (owned) action = `<span class="store-owned"><i class="fa-solid fa-check"></i> ${t("Yours")}</span>`;
    else if (purchaseOn?.itemId === item.id) action = busy(following ? t("Checking…") : t("Paying…"));
    else if (looking) {
        action = busy(t("Looking…"));
        lateButton = "";
    } else if (waiting) {
        // (its wallet step open in this tab, a wallet switched away and back since; being paid in another tab; sent
        // already, from here or elsewhere, or maybe: only ever checked; its wallet step cancelled, or its transaction
        // refused: paid after all, at its price, with time enough left)
        if (payingHere.has(waiting.id)) action = busy(t("Paying…"));
        else if (payingElsewhere(waiting.id)) action = busy(t("Paying in another tab…"));
        else if (sentFor(waiting) || !waiting.pay || !timeToPay(waiting) || maybeSent(waiting.id)) action = checkButton(waiting, t("I paid: check"));
        else action = `<button type="button" class="store-buy" data-pay="${escapeHtml(waiting.id)}" data-item="${escapeHtml(item.id)}">${t("Pay")}</button>${checkButton(waiting, t("Paid it already? Check"), "store-late")}`;
    } else if (!item.onSale) action = busy(t("Not on sale"));
    else if (item.left === 0) action = busy(t("Sold out"));
    else if (!open) action = busy(t("Store closed"));
    else if (mine.walletRequired) action = `<button type="button" class="store-buy" disabled title="${escapeHtml(t("Sign in with your wallet to buy"))}">${t("Sign in to buy")}</button>`;
    else action = `<button type="button" class="store-buy" data-buy="${escapeHtml(item.id)}">${t("Buy")}</button>`;
    // (a payment on its way is at the price it was asked at, whatever the price is now)
    const shownPrice = owned ? "" : waiting ? amount(waiting.priceAtomic) : item.onSale ? amount(item.priceAtomic) : "";
    // (the card takes the focus when none of its buttons can, a purchase on its way: never Close, 0.94 audit. The count
    // left a number, as every other word of the server's is escaped)
    return `<div class="store-card${owned ? " owned" : ""}" data-card="${escapeHtml(item.id)}" tabindex="-1">
        ${art}
        <div class="store-name">${escapeHtml(item.name)}</div>
        ${item.kind === "reaction" && item.text && item.text !== item.name ? `<div class="store-words">“${escapeHtml(item.text)}”</div>` : ""}
        <div class="store-price">${escapeHtml(shownPrice)}${!owned && !waiting && item.onSale && item.left !== undefined ? ` <span class="store-left">· ${Number(item.left) === 1 ? t("1 left") : t("<n> left", { n: Number(item.left) })}</span>` : ""}</div>
        ${action}
        ${lateButton}
        ${note ? `<p class="store-said${note.bad ? " bad" : ""}">${escapeHtml(note.text)}</p>` : waiting?.note ? `<p class="store-said">${escapeHtml(tr(waiting.note))}</p>` : ""}
    </div>`;
};

// ---- wearing: each change sent after the one before, each made from the choice before it (never from boxes drawn
// from an older answer)
let wornChoice: StoreWorn | undefined;
let wearsQueued = 0;
let wearRefused: string | undefined;
let wearing: Promise<void> = Promise.resolve();
const wornNow = (): StoreWorn => wornChoice ?? mine.worn;

const mineTab = (items: readonly StoreItemView[]): string => {
    if (mine.walletRequired) return `<p class="store-empty">${t("Sign in with your wallet to buy and wear store items: they're your wallet's for good.")}</p>`;
    // (the store's emblems, and the flea market's the wallet bought, 0.98: worn as pictures, as the store's are)
    const emblems: ReadonlyArray<{ readonly id: string, readonly name: string, readonly url: string }> = [
        ...items.filter(item => item.kind === "emblem" && mine.owned.includes(item.id)),
        ...MarketMenu.bought().filter(item => item.wearable)
    ];
    const reactions = items.filter(item => item.kind === "reaction" && mine.owned.includes(item.id));
    if (!emblems.length && !reactions.length) return `<p class="store-empty">${t("Nothing yet: what you buy shows here, to wear.")}</p>`;
    const worn = wornNow();
    const key = reactionKey();
    // (a phone has no key for it: the reactions button opens it there; 0.94 audit, phones were told to hold X)
    const how = InputManager.isMobile
        ? t("tap the reactions button at the top left, then tap one")
        : key ? t("hold <key>, point at one, let go", { key: escapeHtml(key) }) : t("give them a key in Settings → Keybinds first");
    const emblemRows = emblems.length
        ? `<h4>${t("Emblem on your uniform")}</h4><div class="store-wear-list">
            <label class="store-wear"><input type="radio" name="store-emblem" value="" ${worn.emblem ? "" : "checked"}><span>${t("My own drawing")}</span></label>
            ${emblems.map(item => `<label class="store-wear"><input type="radio" name="store-emblem" value="${escapeHtml(item.id)}" ${worn.emblem === item.id ? "checked" : ""}><img src="${escapeHtml(item.url)}" alt=""><span>${escapeHtml(item.name)}</span></label>`).join("")}
        </div>`
        : "";
    const reactionRows = reactions.length
        ? `<h4>${t("Voice reactions in a match")} <small>${t("(up to <n>: <how>)", { n: STORE_LIMITS.maxReactionsWorn, how })}</small></h4><div class="store-wear-list">
            ${reactions.map(item => {
                const place = worn.reactions.indexOf(item.id);
                const words = escapeHtml(item.text ?? item.name);
                // (each box and button named by its reaction: the buttons were all "Play", the boxes "Play Hey there! 1";
                // 0.94 audit. In the player's language, each name escaped whole)
                const box = place >= 0 ? escapeHtml(t("<reaction>, slot <n> on the wheel", { n: place + 1, reaction: item.text ?? item.name })) : words;
                const play = escapeHtml(t("Play <name>", { name: item.text ?? item.name }));
                return `<label class="store-wear"><input type="checkbox" name="store-reaction" value="${escapeHtml(item.id)}" aria-label="${box}" ${place >= 0 ? "checked" : ""}><button type="button" class="store-play small" data-play="${escapeHtml(item.id)}" aria-label="${play}"><i class="fa-solid fa-volume-high"></i></button><span>${words}</span>${place >= 0 ? `<em>${place + 1}</em>` : ""}</label>`;
            }).join("")}
        </div>`
        : "";
    return `${emblemRows}${reactionRows}<p class="store-said" id="store-wear-said"></p>`;
};

/**
 * the cards drawn again when the first hold they show ends (0.93 review: nothing else may draw them then): another
 * tab's paying mark (5 minutes), a maybe-sent one (10 minutes), Pay's last five minutes
 */
let holdTimer: ReturnType<typeof setTimeout> | undefined;
const renderWhenHoldsEnd = (): void => {
    clearTimeout(holdTimer);
    holdTimer = undefined;
    const now = Date.now();
    const paying = readRecord<number>(PAYING_KEY);
    const maybe = readRecord<number>(MAYBE_KEY);
    let next = Number.POSITIVE_INFINITY;
    for (const payment of mine.payments) {
        if (payment.state !== "waiting") continue;
        for (const at of [Number(paying[payment.id]) + PAYING_ELSEWHERE_MS, Number(maybe[payment.id] ?? maybeHere.get(payment.id)) + MAYBE_SENT_MS, payment.expiresAt - clockSkew - PAY_MARGIN_MS])
            if (Number.isFinite(at) && at > now) next = Math.min(next, at);
    }
    // (the flea market's cards' holds too, 0.98)
    const market = MarketMenu.holdsEnd();
    if (market !== undefined && market > now) next = Math.min(next, market);
    // (a little after it ends; at most half an hour on, drawn again then)
    if (next < Number.POSITIVE_INFINITY) holdTimer = setTimeout(render, Math.min(next - now + 250, 30 * 60_000));
};

interface Focused {
    readonly selector?: string
    readonly card?: string
    /** the card itself had it */
    readonly onCard?: boolean
}
/** what has the focus in the list, to give it back after the list is drawn again (the list itself keeps it) */
const focusOf = (list: HTMLElement): Focused | undefined => {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || active === list || !list.contains(active)) return undefined;
    const card = active.closest<HTMLElement>("[data-card]")?.dataset.card;
    for (const name of ["buy", "pay", "check", "play", "retry", "marketBuy", "marketPay", "marketCheck", "marketView", "marketMore", "marketSell", "marketSave", "marketToggle", "marketDelete"] as const) {
        const value = active.dataset[name];
        if (value) return { selector: `[data-${name.replace(/[A-Z]/gu, letter => `-${letter.toLowerCase()}`)}="${CSS.escape(value)}"]`, card };
    }
    // (the flea market's sort: its own control, kept as the list is drawn again; 0.98 review)
    if (active instanceof HTMLSelectElement && active.dataset.marketSort !== undefined) return { selector: "[data-market-sort]", card };
    if (active instanceof HTMLInputElement && active.name) return { selector: `input[name="${CSS.escape(active.name)}"][value="${CSS.escape(active.value)}"]`, card };
    return { card, onCard: active.dataset.card !== undefined };
};
/**
 * the focus where it was: a status drawn every few seconds never drops a keyboard player to the page, nor puts them on
 * Close (0.94 audit: Enter or Space then closed the store, a payment on its way). The same control, else another of its
 * card's, else its card, else the list
 */
const focusAgain = (list: HTMLElement, focused: Focused | undefined): void => {
    if (!focused) return;
    const card = focused.card ? list.querySelector<HTMLElement>(`[data-card="${CSS.escape(focused.card)}"]`) : null;
    const again = (focused.selector ? list.querySelector<HTMLElement>(`${focused.selector}:not([disabled])`) : null)
        ?? (focused.onCard ? card : card?.querySelector<HTMLElement>("button:not([disabled])"))
        ?? card
        ?? list;
    again.focus({ preventScroll: true });
};

/**
 * the list drawn again from its markup, its pictures kept: each one drawn the same is the element already shown, moved
 * into place (0.94 audit: every redraw made every picture again, each blank until it was drawn: every 4 s while a
 * payment is followed). Made in this page's document (a template's is another: a picture moved there is read again)
 */
const draw = (list: HTMLElement, html: string): void => {
    const pictures = new Map<string, HTMLImageElement[]>();
    list.querySelectorAll("img").forEach(image => pictures.set(image.outerHTML, [...(pictures.get(image.outerHTML) ?? []), image]));
    const next = document.createElement("div");
    next.innerHTML = html;
    next.querySelectorAll("img").forEach(image => {
        const same = pictures.get(image.outerHTML)?.shift();
        if (same) image.replaceWith(same);
    });
    list.replaceChildren(...Array.from(next.childNodes));
};

const render = (): void => {
    const view = StoreCatalog.view;
    const list = element("store-items");
    const note = element("store-note");
    if (!list || !note) return;
    // (the tabs as ARIA has them: Tab stops on the one shown, the arrows go to the others; 0.94 audit)
    document.querySelectorAll<HTMLButtonElement>("#store-tabs [data-store-tab]").forEach(button => {
        button.setAttribute("aria-selected", String(button.dataset.storeTab === tab));
        button.tabIndex = button.dataset.storeTab === tab ? 0 : -1;
    });
    list.setAttribute("aria-labelledby", `store-tab-${tab}`);
    const focused = focusOf(list);
    // the flea market (0.98): its own list, drawn by marketMenu.ts (never under the player's typing in its fields)
    if (tab === "market") {
        note.textContent = MarketMenu.note();
        if (MarketMenu.typing(list)) return;
        draw(list, MarketMenu.html());
        MarketMenu.afterDraw(list);
        focusAgain(list, focused);
        renderWhenHoldsEnd();
        return;
    }
    if (!view) {
        // (one that couldn't be read says so, and is read again at the player's word: it said "Loading the store…" for
        // ever; 0.94 audit)
        const failed = StoreCatalog.failed;
        note.textContent = failed ? t("The store couldn't be loaded just now: check your connection and try again.") : t("Loading the store…");
        draw(list, failed ? `<p class="store-empty"><button type="button" class="store-buy" data-retry="store">${t("Try again")}</button></p>` : "");
        focusAgain(list, focused);
        return;
    }
    const symbol = view.token?.symbol ?? t("the game's token");
    note.textContent = view.problem
        ? tr(view.problem)
        : !view.open
            ? t("The store is closed right now: look at what's coming.")
            : view.burnMethod === "transfer"
                ? t("Paid in <coin> from your wallet. The price is burned (sent to the dead address), and the item is yours for good.", { coin: symbol })
                // (the coin's own burn, 0.95: its supply goes down by the price)
                : t("Paid in <coin> from your wallet: it burns the price (taken out of <coin>'s supply), and the item is yours for good.", { coin: symbol });
    // (an item with a payment on its way, or one to look for, stays shown after it's taken off sale or off the store;
    // one whose card says something too, until the store is closed: a purchase the store refused, the item taken off
    // sale meanwhile, says so on it)
    const kept = (item: StoreItemView): boolean => mine.owned.includes(item.id) || !!waitingFor(item.id) || !!lateFor(item.id) || said.has(item.id);
    const shown = view.items.filter(item => !item.removed || kept(item));
    if (tab === "mine") draw(list, mineTab(shown));
    else {
        const items = shown.filter(item => item.kind === tab && (item.onSale || kept(item)));
        draw(list, items.length
            ? `<div class="store-grid">${items.map(card).join("")}</div>`
            : `<p class="store-empty">${tab === "emblem" ? t("No emblems on sale yet.") : t("No voice reactions on sale yet.")}</p>`);
    }
    if (tab === "mine" && wearRefused) {
        const shownSaid = element("store-wear-said");
        if (shownSaid) {
            shownSaid.textContent = wearRefused;
            shownSaid.classList.add("bad");
        }
    }
    focusAgain(list, focused);
    renderWhenHoldsEnd();
};

/** said to a screen reader (the store's own live region): what a card or the Mine tab says (0.94 audit: none was) */
const announce = (text: string): void => {
    const live = element("store-announce");
    if (live) live.textContent = text;
};

const say = (itemId: string, text: string, bad = false): void => {
    const before = said.get(itemId)?.text;
    said.set(itemId, { text, bad });
    render();
    if (text !== before) announce(t("<item>: <text>", { item: StoreCatalog.item(itemId)?.name ?? t("Store"), text }));
};

const lateWords = (): string => t("Your payment went out after its time was up. It still counts if it went in a block within 15 minutes of the end, or while the item is on sale at that price: press “Paid it already? Look again” in a minute. Don't pay again.");
const onItsWayWords = (): string => t("Your earlier payment may still go through: look again in a minute (don't pay again).");
const refusedWords = (): string => t("The store didn't take that transaction: nothing was counted");
const ownedWords = (): string => t("You have this item already: that transfer wasn't counted for it again");
const alreadyYoursWords = (): string => t("You have this item already (another payment of yours paid for it): this one isn't counted. Don't pay again.");
const couldNotLookWords = (): string => t("The store couldn't look for your payment just now: try again in a minute (nothing was paid).");
const couldNotLookYetWords = (): string => t("The store couldn't look for your payment just now: try again in a minute (don't pay again yet).");
const walletOpenWords = (): string => t("Your wallet is asked to pay it already: answer it there first.");

/** a payment settled, as the store answered it: what its card says (nothing, another wallet signed in meanwhile) */
const settled = async(payment: StorePaymentView, itemId: string, run: Run): Promise<void> => {
    await loadMine();
    stillOn(run);
    if (payment.state === "paid") say(itemId, t("It's yours! Wear it in Mine."));
    else if (mine.owned.includes(payment.itemId)) say(itemId, serverWords(payment.note) ?? ownedWords(), true);
    else if (payment.state === "waiting") say(itemId, t("Your payment isn't in yet: press “I paid: check” in a minute (don't pay again: it counts once it's in)."));
    else say(itemId, serverWords(payment.note) ?? t("No payment came: nothing was taken."), true);
};

/**
 * a look the store made whole, its answer said (0.93 review: one answered as it was, asked too soon after another look,
 * can't see a transfer in a block): asked again a moment on, three times at most. One that found it paid, or that knows
 * a transaction for it, needs no other (a payment given up may still be paid late: looked at whole too)
 */
const lookedUp = async(run: Run, request: () => Promise<Answer>): Promise<Answer> => {
    for (let tries = 1; ; tries++) {
        const answer = await request();
        stillOn(run);
        if (answer.looked !== false || answer.payment.state === "paid" || answer.payment.tx || tries >= 3) return answer;
    }
};

/**
 * a payment the store answered with after it was told a transaction: whether it kept it, refused it (dropped: told
 * again before the wallet pays again), or couldn't take it, its payment's time up (kept: it may count all the same)
 */
const afterTold = (answer: Answer, tx: string): { readonly refused?: string, readonly late?: boolean, readonly owned?: string } => {
    const payment = answer.payment;
    if (payment.tx?.toLowerCase() === tx) {
        rememberSent(payment.id, tx, true);
        return {};
    }
    if (payment.state === "paid") return {};
    // (it paid nothing: the wallet has the item already. Nothing more to pay, ever)
    if (answer.told === "owned") {
        forgetSent(payment.id);
        return { owned: serverWords(payment.note) ?? ownedWords() };
    }
    if (answer.told === "refused") {
        dropSent(payment.id);
        return { refused: serverWords(payment.note) ?? refusedWords() };
    }
    if (payment.state !== "waiting" || payment.expiresAt <= storeNow()) return { late: true };
    return {};
};

/** the payment looked at until it's paid (a few seconds, its transaction in a block), at most two minutes */
const followPayment = async(payment: StorePaymentView, itemId: string, run: Run): Promise<void> => {
    following = true;
    render();
    const deadline = Date.now() + 2 * 60_000;
    let current = payment;
    // (the transaction followed: the store's word for it, else this device's)
    let followed = payment.tx?.toLowerCase() ?? sentOf(payment.id)?.tx;
    while (current.state === "waiting" && Date.now() < deadline) {
        await sleep(4_000);
        stillOn(run);
        let answer: Answer;
        try {
            answer = await look(current.id);
        } catch (error) {
            stillOn(run);
            say(itemId, t("Checking your payment: <error>", { error: (error as Error).message }));
            continue;
        }
        stillOn(run);
        current = answer.payment;
        keepPayment(current);
        if (current.tx) followed = current.tx.toLowerCase();
        // (the store had its transaction and has it no more: refused (it failed), or lost (a restart: another one it had
        // may be refused in its place). This device's is told again before the wallet is asked again; another device's
        // holds Pay back a while, as one the wallet may have sent does)
        if (current.state === "waiting" && !current.tx) {
            const ours = sentOf(current.id)?.tx;
            const refused = answer.told === "refused" && answer.toldTx !== undefined && answer.toldTx === followed;
            if (ours) dropSent(current.id);
            // (another device's lost: its page tells it again, Pay held back meanwhile)
            const othersLost = !refused && followed !== undefined && followed !== ours;
            if (othersLost) markMaybeSent(current.id);
            await loadMine();
            stillOn(run);
            say(itemId, refused
                ? serverWords(current.note) ?? refusedWords()
                : othersLost
                    ? t("The store has lost track of the transaction sent for it: look again in a minute (the page that sent it tells it again). Don't pay again yet.")
                    : t("The store has lost track of your transaction: press Pay to tell it again (your wallet is asked again only if it's refused)."), true);
            return;
        }
        if (current.state === "waiting" && current.note) say(itemId, tr(current.note));
    }
    await settled(current, itemId, run);
};

/** the store told the transaction the wallet sent (kept on this device first); it's followed until it's in */
const reportSent = async(payment: StorePaymentView, tx: string, itemId: string, run: Run): Promise<void> => {
    say(itemId, t("Sent: waiting for it to go in a block…"));
    let answer: Answer;
    try {
        answer = await tell(payment.id, tx);
    } catch (error) {
        stillOn(run);
        // (the wallet sent it: never asked again. The next check tells the store this transaction)
        await loadMine();
        stillOn(run);
        say(itemId, t("Your payment was sent, but the store couldn't be told yet (<error>): press “I paid: check” in a moment. Don't pay again.", { error: (error as Error).message }), true);
        return;
    }
    stillOn(run);
    keepPayment(answer.payment);
    const { refused, late, owned } = afterTold(answer, tx);
    if (refused || late || owned) {
        await loadMine();
        stillOn(run);
        // (the item the wallet's already, another payment paid it meanwhile: this one isn't counted)
        say(itemId, owned ?? (mine.owned.includes(itemId) ? alreadyYoursWords() : refused ?? lateWords()), true);
    } else if (answer.payment.state === "waiting") await followPayment(answer.payment, itemId, run);
    else await settled(answer.payment, itemId, run);
};

/** the wallet asked to send a payment's price, once: its transaction remembered before anything else */
const payWith = async(payment: StorePaymentView, pay: PayInstruction, itemId: string, run: Run): Promise<void> => {
    if (!timeToPay(payment)) throw new Error(t("That payment's time is nearly up: buy it again once it has ended (nothing was paid)."));
    if (payingHere.has(payment.id)) throw new Error(walletOpenWords());
    if (payingElsewhere(payment.id)) throw new Error(t("It's being paid in another tab: look there, or wait a moment."));
    stillOn(run);
    let tx: string;
    markPaying(payment.id);
    try {
        tx = (await sendStorePayment(pay, text => {
            if (alive(run)) say(itemId, text);
        })).toLowerCase();
    } catch (error) {
        const failure = error as { message?: string, cancelled?: boolean, notSent?: boolean };
        const words = failure.message ?? String(error);
        // (a refusal in the wallet, or an error before it was asked to send: nothing was sent, Pay pays it after all)
        if (failure.cancelled || (!failure.notSent && /\b(?:reject(?:s|ed|ion)?|denied|cancel(?:s|l?ed|l?ing|lation)?)\b/iu.test(words))) throw new Error(t("You cancelled it in your wallet: nothing was paid (Pay to pay it after all)."));
        if (failure.notSent) throw new Error(words);
        // (an error once the wallet was asked to send: it may have sent it all the same. Only looked for a while)
        markMaybeSent(payment.id);
        throw new Error(t("Your wallet answered: <words> It may have sent your payment all the same: press “I paid: check” in a minute, and don't pay again yet.", { words }));
    } finally {
        clearPaying(payment.id);
    }
    if (!HASH.test(tx)) {
        markMaybeSent(payment.id);
        throw new Error(t("Your wallet's answer wasn't a transaction: press “I paid: check” in a minute, and don't pay again yet."));
    }
    rememberSent(payment.id, tx);
    stillOn(run);
    await reportSent(payment, tx, itemId, run);
};

/** one purchase at a time: its card busy (Paying…, then Checking…) until it's settled or left to the store's checks */
const purchase = async(itemId: string, first: string, work: (run: Run) => Promise<void>): Promise<void> => {
    if (purchaseOn) {
        if (purchaseOn.itemId !== itemId) say(itemId, t("Finish the payment on its way first."), true);
        return;
    }
    const run = startRun();
    const id = ++purchases;
    purchaseOn = { id, itemId };
    following = false;
    say(itemId, first);
    try {
        await work(run);
    } catch (error) {
        if (error instanceof Stopped || !alive(run)) return;
        await loadMine();
        if (alive(run)) say(itemId, (error as Error).message, true);
    } finally {
        if (purchaseOn?.id === id) {
            purchaseOn = undefined;
            following = false;
        }
        render();
    }
};

/** a payment to make (a new one, or one waiting already): told the store, followed, or paid by the wallet */
const go = async(payment: StorePaymentView, pay: PayInstruction | undefined, itemId: string, run: Run): Promise<void> => {
    // (its transaction known: to the store (another page, another device: followed), or to this device only (told))
    if (payment.tx) return followPayment(payment, itemId, run);
    const sent = sentFor(payment);
    if (sent) return reportSent(payment, sent, itemId, run);
    // (one this device sent that the store dropped: told once more first. Lost (a restart) and still on its way, or in a
    // block, it's followed; refused again (it failed), it's forgotten and the wallet pays)
    let current = payment;
    const dropped = droppedFor(payment.id);
    if (dropped) {
        say(itemId, t("Looking at your earlier payment first…"));
        const answer = await lookedUp(run, () => tell(payment.id, dropped, true));
        current = answer.payment;
        keepPayment(current);
        if (current.state !== "waiting" || answer.told === "owned") return settled(current, itemId, run);
        if (current.tx) {
            if (current.tx.toLowerCase() === dropped) rememberSent(payment.id, dropped, true);
            return followPayment(current, itemId, run);
        }
        // (never forgotten unlooked, nor unless the store refused that one: it was sent, it may still go in; refused, the
        // store's search for another transfer cut short, nothing is paid yet)
        if (answer.looked === false) throw new Error(answer.told === "refused" ? couldNotLookWords() : onItsWayWords());
        if (answer.told !== "refused" && Date.now() - (sentOf(payment.id)?.at ?? 0) < STILL_ON_ITS_WAY_MS) throw new Error(onItsWayWords());
        forgetSent(payment.id);
    }
    if (maybeSent(payment.id)) throw new Error(t("Your wallet may have sent it already: press “I paid: check” first, and don't pay again yet."));
    if (pay) return payWith(current, pay, itemId, run);
    throw new Error(t("The store didn't say how to pay it: close the store and open it again"));
};

const buy = (itemId: string): Promise<void> => purchase(itemId, t("Asking the store…"), async run => {
    // (a transaction this device sent a moment ago for a payment of the item given up since: told once more first. It
    // may count all the same, late; a new payment, and the wallet asked again, only once the store refuses it)
    const late = lateSentFor(itemId);
    if (late) {
        say(itemId, t("Looking at your earlier payment first…"));
        const answer = await lookedUp(run, () => tell(late.payment.id, late.tx, true));
        keepPayment(answer.payment);
        if (answer.payment.state === "paid" || answer.told === "owned") return settled(answer.payment, itemId, run);
        // (not looked at, or not refused: it was sent, it may still count)
        if (answer.looked === false || answer.told !== "refused") throw new Error(onItsWayWords());
        forgetSent(late.payment.id);
    }
    // (refused: the item may have been taken off sale, sold out or repriced since the catalogue was read. It's read again,
    // and its card says so: it kept its Buy and its old price; 0.94 audit)
    const { payment, pay } = await api<{ payment: StorePaymentView, pay?: PayInstruction }>("/store/buy", { method: "POST", body: JSON.stringify({ itemId }) })
        .catch(async(error: unknown) => {
            await StoreCatalog.load();
            throw error;
        });
    stillOn(run);
    keepPayment({ ...payment, ...(pay ? { pay } : {}) });
    if (!pay && !payment.tx && !sentFor(payment) && !droppedFor(payment.id)) throw new Error(t("The store isn't set up yet"));
    await go(payment, pay, itemId, run);
});

/** a payment waiting whose wallet step was cancelled (or whose transaction was refused): looked for, then paid */
const payAgain = (paymentId: string, itemId: string): Promise<void> => purchase(itemId, t("Looking for it first…"), async run => {
    if (checking.has(paymentId)) throw new Error(t("It's being looked for: wait for the answer first."));
    if (payingHere.has(paymentId)) throw new Error(walletOpenWords());
    if (payingElsewhere(paymentId)) throw new Error(t("It's being paid in another tab: look there, or wait a moment."));
    // (a whole look of its own first: a transfer in a block for it, sent from elsewhere, pays it)
    const answer = await lookedUp(run, () => look(paymentId, true));
    const pay = mine.payments.find(other => other.id === paymentId)?.pay;
    keepPayment(answer.payment);
    // (not looked at whole: given up meanwhile, it may still be paid late, unless the item is the wallet's already;
    // waiting, the wallet isn't asked)
    if (answer.looked === false && answer.payment.state === "expired") {
        await loadMine();
        stillOn(run);
        if (!mine.owned.includes(answer.payment.itemId)) throw new Error(couldNotLookYetWords());
    }
    if (answer.payment.state !== "waiting") return settled(answer.payment, itemId, run);
    if (answer.looked === false && !answer.payment.tx) throw new Error(couldNotLookWords());
    await go(answer.payment, pay, itemId, run);
});

/** a payment looked for at the player's word: the transaction this device sent told, else the coin's records searched */
const check = async(paymentId: string, itemId: string): Promise<void> => {
    if (checking.has(paymentId)) return;
    const run = startRun();
    checking.add(paymentId);
    say(itemId, t("Looking for your payment…"));
    try {
        // (the transaction this device sent, if the store wasn't told it, or dropped it: told now, which checks it too)
        const known = mine.payments.find(payment => payment.id === paymentId);
        const dropped = droppedFor(paymentId);
        const sent = known && !known.tx ? sentFor(known) ?? dropped : undefined;
        const answer = await lookedUp(run, () => sent ? tell(paymentId, sent, true) : look(paymentId, true));
        const told = sent ? afterTold(answer, sent) : undefined;
        const refused = told?.refused;
        const late = told?.late;
        // (a dropped one refused again: forgotten, the payment can be paid)
        if (refused && sent === dropped) forgetSent(paymentId);
        checking.delete(paymentId);
        await loadMine();
        stillOn(run);
        const payment = answer.payment;
        if (payment.state === "paid") say(itemId, t("It's yours! Wear it in Mine."));
        else if (told?.owned) say(itemId, told.owned, true);
        else if (mine.owned.includes(itemId)) say(itemId, serverWords(payment.note) ?? alreadyYoursWords(), true);
        else if (refused) say(itemId, refused, true);
        else if (late) say(itemId, lateWords(), true);
        // (not looked at whole: a payment given up may still be paid late, never "nothing came")
        else if (answer.looked === false) say(itemId, couldNotLookYetWords(), payment.state !== "waiting");
        else say(itemId, serverWords(payment.note) ?? (payment.state === "waiting" ? t("Not found yet: try again in a minute.") : t("No payment came: nothing was taken.")), payment.state !== "waiting");
    } catch (error) {
        checking.delete(paymentId);
        if (!(error instanceof Stopped) && alive(run)) say(itemId, (error as Error).message, true);
    } finally {
        checking.delete(paymentId);
        render();
    }
};

const wearNow = async(change: { emblem?: string | null, reactions?: string[] }, run: Run): Promise<void> => {
    // (one refused: the ones queued after it, made from it, aren't sent; what the store has is shown)
    if (wearRefused || !alive(run)) return;
    try {
        const { worn } = await api<{ worn: StoreWorn }>("/store/wear", { method: "PUT", body: JSON.stringify(change) });
        if (!alive(run)) return;
        mine = { ...mine, worn };
    } catch (error) {
        if (alive(run)) wearRefused = (error as Error).message;
    }
};
const wear = (change: { emblem?: string | null, reactions?: string[] }, choice: StoreWorn): void => {
    const run = startRun();
    wornChoice = choice;
    wearsQueued++;
    wearing = wearing.then(() => wearNow(change, run)).finally(() => {
        if (--wearsQueued > 0) return;
        const refused = wearRefused;
        wornChoice = undefined;
        void showWornPicture();
        render();
        wearRefused = undefined;
        // (another wallet signed in meanwhile: nothing of the one before's said)
        if (!alive(run)) return;
        const words = refused ?? t("Saved: you wear it from your next match.");
        const shown = element("store-wear-said");
        if (shown) {
            shown.textContent = words;
            shown.classList.toggle("bad", refused !== undefined);
        }
        announce(words);
    });
};

const stopPreview = (): void => {
    preview?.pause();
    preview = undefined;
    clearTimeout(previewTimer);
};

const play = (itemId: string): void => {
    const item = StoreCatalog.item(itemId);
    if (item?.kind !== "reaction") return;
    stopPreview();
    // (this clip cut after its 4 s: never the one played after it)
    const clip = new Audio(item.url);
    preview = clip;
    // (as loud as the game's sounds are set: Master × Sound effects; 0.94 audit, it ignored them)
    let volume = 0.8;
    try {
        volume *= Number(GameConsole.getBuiltInCVar("cv_master_volume")) * Number(GameConsole.getBuiltInCVar("cv_sfx_volume"));
    } catch {
        /* (the settings not read yet: as it was) */
    }
    clip.volume = Number.isFinite(volume) ? Math.min(1, Math.max(0, volume)) : 0.8;
    void clip.play().catch(() => undefined);
    previewTimer = setTimeout(() => clip.pause(), STORE_LIMITS.maxClipMs);
};

const visible = (): boolean => $("#store-modal").is(":visible");

export const StoreMenu = {
    /** the reactions the player takes into a match (the wheel's), as the server knows them */
    get reactions(): readonly string[] {
        return mineFor !== undefined && mineFor === storeBuyer()?.address ? mine.worn.reactions : [];
    },

    /** the player's own items read again (signed in, a match over) */
    async refresh(): Promise<void> {
        await Promise.all([StoreCatalog.load(), loadMine(), MarketMenu.refresh()]);
        void showWornPicture();
        render();
    },

    async open(): Promise<void> {
        const modal = element("store-modal");
        if (!modal) return;
        said.clear();
        if (!visible()) {
            const active = document.activeElement;
            opener = active instanceof HTMLElement && active !== document.body ? active : element("btn-store") ?? undefined;
        }
        $(".dialog").not(modal).fadeOut(150);
        modal.classList.remove("closing");
        $(modal).fadeIn(200, () => element("close-store")?.focus());
        // (read again first: a catalogue that couldn't be read before says "Loading the store…" meanwhile)
        const reading = this.refresh();
        render();
        await reading;
    },

    /** closed (`giveFocusBack`: the focus back where it was, as when the player closes it; not when a match starts) */
    close(giveFocusBack = true): void {
        stopPreview();
        if (!visible()) return;
        const back = giveFocusBack ? opener : undefined;
        opener = undefined;
        // (the page behind it takes clicks again at once, a match starting too: its backdrop goes as it starts to fade)
        element("store-modal")?.classList.add("closing");
        $("#store-modal").fadeOut(200, () => {
            // (unless the player has moved on meanwhile)
            const active = document.activeElement;
            if (back?.isConnected && (!active || active === document.body || element("store-modal")?.contains(active))) back.focus({ preventScroll: true });
        });
    },

    init(): void {
        lastBuyer = storeBuyer()?.address;
        MarketMenu.attach({ redraw: render, announce });
        element("btn-store")?.addEventListener("click", () => void this.open());
        element("close-store")?.addEventListener("click", () => this.close());
        const modal = element("store-modal");
        // the page behind it takes no clicks while it shows (0.94 audit: the menu's Customize, under the Mine tab, opened
        // the emblem editor under it): its backdrop is shown with it and hidden with it, however it's hidden (another
        // dialog's button hides them all), and from the moment it starts to close
        const backdrop = element("store-backdrop");
        if (modal && backdrop) {
            const follow = (): void => {
                backdrop.hidden = modal.style.display === "none" || modal.classList.contains("closing");
            };
            new MutationObserver(follow).observe(modal, { attributes: true, attributeFilter: ["style", "class"] });
            follow();
            // (a click on it leaves the focus in the store: on the page, Tab went on to the menu behind)
            backdrop.addEventListener("mousedown", event => event.preventDefault());
        }
        modal?.addEventListener("keydown", event => {
            if (event.key === "Escape") this.close();
            // a modal dialog: Tab and Shift+Tab go round inside it, not to the menu behind it
            if (event.key === "Tab") {
                // (the tabs not shown are left out, as Tab leaves them out)
                const inside = Array.from(modal.querySelectorAll<HTMLElement>("a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])"))
                    .filter(each => each.tabIndex >= 0 && each.getClientRects().length > 0);
                const first = inside[0];
                const last = inside[inside.length - 1];
                const active = document.activeElement;
                if (first && last && (event.shiftKey ? active === first || !modal.contains(active) : active === last || !modal.contains(active))) {
                    event.preventDefault();
                    (event.shiftKey ? last : first).focus();
                }
            }
            // (keys typed here never move a player or open the console)
            event.stopPropagation();
        });
        // (Escape closes it wherever the focus went)
        document.addEventListener("keydown", event => {
            if (event.key === "Escape" && visible() && !modal?.contains(document.activeElement)) this.close();
        });
        // (another tab paid, sent or marked a payment: this one's cards say so)
        if (typeof window !== "undefined") {
            window.addEventListener("storage", event => {
                if (event.key === SENT_KEY || event.key === MAYBE_KEY || event.key === PAYING_KEY || (event.key !== null && MarketMenu.storageKeys.includes(event.key))) render();
            });
        }
        const tabs = Array.from(document.querySelectorAll<HTMLButtonElement>("#store-tabs [data-store-tab]"));
        tabs.forEach(button => {
            button.addEventListener("click", () => {
                tab = (button.dataset.storeTab as Tab | undefined) ?? "emblem";
                render();
            });
        });
        // (the arrows, Home and End go from tab to tab, each shown as it's reached: ARIA's tabs; 0.94 audit)
        element("store-tabs")?.addEventListener("keydown", event => {
            const at = tabs.indexOf(event.target as HTMLButtonElement);
            const moves: Partial<Record<string, number>> = { ArrowRight: at + 1, ArrowLeft: at - 1, Home: 0, End: tabs.length - 1 };
            const to = moves[event.key];
            const next = at >= 0 && to !== undefined ? tabs[(to + tabs.length) % tabs.length] : undefined;
            if (!next) return;
            event.preventDefault();
            next.focus();
            next.click();
        });
        element("store-items")?.addEventListener("click", event => {
            // (the flea market's own buttons, 0.98)
            if (event.target instanceof HTMLElement && MarketMenu.click(event.target)) return;
            const target = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-buy], [data-check], [data-pay], [data-play], [data-retry]");
            if (!target) return;
            if (target.dataset.retry) {
                // (the catalogue read again, "Loading the store…" meanwhile)
                void this.refresh();
                render();
            } else if (target.dataset.play) {
                event.preventDefault();
                play(target.dataset.play);
            } else if (target.dataset.buy) void buy(target.dataset.buy);
            else if (target.dataset.pay && target.dataset.item) void payAgain(target.dataset.pay, target.dataset.item);
            else if (target.dataset.check && target.dataset.item) void check(target.dataset.check, target.dataset.item);
        });
        // (the flea market's fields, kept as typed; the list drawn again once the player leaves one)
        element("store-items")?.addEventListener("input", event => {
            if (event.target instanceof HTMLInputElement) MarketMenu.change(event.target);
        });
        // (not under a press: a button pressed to leave the field was drawn anew before it was let go, and the press lost:
        // every sale took two clicks; 0.98 review. Drawn once the click is done, or a moment after a press that made none)
        let pressing = false;
        let drawAfterPress = false;
        const drawPressed = (): void => {
            if (!drawAfterPress) return;
            drawAfterPress = false;
            render();
        };
        element("store-items")?.addEventListener("pointerdown", () => {
            pressing = true;
        }, true);
        const letGo = (): void => {
            if (!pressing) return;
            pressing = false;
            if (drawAfterPress) setTimeout(drawPressed, 300);
        };
        document.addEventListener("pointerup", letGo, true);
        document.addEventListener("pointercancel", letGo, true);
        document.addEventListener("click", () => {
            if (drawAfterPress) setTimeout(drawPressed, 0);
        });
        element("store-items")?.addEventListener("focusout", event => {
            const left = event.target;
            if (!(left instanceof HTMLInputElement && (left.dataset.marketField !== undefined || left.dataset.marketEdit !== undefined))) return;
            if (pressing) drawAfterPress = true;
            else setTimeout(render, 0);
        });
        element("store-items")?.addEventListener("change", event => {
            if ((event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) && MarketMenu.change(event.target)) return;
            const input = event.target as HTMLInputElement;
            const worn = wornNow();
            if (input.name === "store-emblem") {
                const emblem = input.value || undefined;
                wear({ emblem: emblem ?? null }, { ...worn, emblem });
            } else if (input.name === "store-reaction") {
                // (from the choice before this one, the boxes ticked in the order they were: the ones worn keep their places)
                const reactions = input.checked
                    ? worn.reactions.includes(input.value) ? [...worn.reactions] : [...worn.reactions, input.value]
                    : worn.reactions.filter(id => id !== input.value);
                if (reactions.length > STORE_LIMITS.maxReactionsWorn) {
                    input.checked = false;
                    const words = t("At most <n>: untick one first.", { n: STORE_LIMITS.maxReactionsWorn });
                    const shown = element("store-wear-said");
                    if (shown) shown.textContent = words;
                    announce(words);
                    return;
                }
                wear({ reactions }, { ...worn, reactions });
            }
        });
        storeBuyerChanged.addEventListener("change", () => {
            // another wallet: what the one before had on its way stops (nothing of it paid, said or kept for this one)
            const now = storeBuyer()?.address;
            if (now !== lastBuyer) {
                lastBuyer = now;
                generation++;
                purchaseOn = undefined;
                following = false;
                checking.clear();
                wornChoice = undefined;
                wearRefused = undefined;
            }
            void this.refresh();
        });
        void this.refresh();
    }
};
